<template>
  <SfDropdown
    :is-open="isScopedDropdownOpen"
    :dropdown-id="dropdownId"
    variant="seamless"
    unstyled-trigger
    @dropdown-clicked="handleToggleDropdown"
    @dropdown-closed="dropdownStore.closeDropdown"
  >
    <template #trigger>
      <SfButtonDs
        icon-after="caret-down"
        class="tw-max-w-[200px] sf-button--full-width-label"
      >
        <span class="tw-truncate">{{ triggerDisplayValue }}</span>

        <span
          v-if="selectedValues.length > 0"
          class="fa fa-remove tw-ml-2 tw-shrink-0"
          data-testid="sf-search-clear"
          @click.prevent.stop="clearSelected"
        />
      </SfButtonDs>
    </template>

    <template #menu>
      <div class="tw-p-2 tw-overflow-hidden">
        <div class="tw-mb-2">
          <SfSearch
            v-model="searchValue"
            :show-search-icon="true"
            :show-clear-icon="true"
            :show-result-navigation="false"
            :placeholder="placeholder"
            :bottom-margin="true"
            @clear="clearSearch"
          />
        </div>
        <div v-for="(group, index) in collection" :key="group.id">
          <SfDropdownItemGroup
            :with-separator="index !== collection.length - 1"
          >
            <template v-if="index === 0">
              <slot name="before" />
            </template>

            <SfDropdownItem
              v-for="item in group.options"
              :key="item.value"
              :is-wrapped="false"
              :has-border="false"
              :is-rounded="true"
              :has-checkmark="isSelected(item.value)"
              :class="{ 'tw-font-medium': isSelected(item.value) }"
              class="tw-leading-3"
              @click="toggleSelection(item.value)"
            >
              <span
                v-if="item.icon"
                :class="`fa-${item.icon} tw-text-${item.iconColor}`"
                class="fa tw-mr-3"
              />
              <span>{{ item.label }}</span>
            </SfDropdownItem>
          </SfDropdownItemGroup>
        </div>
      </div>
    </template>
  </SfDropdown>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useVModel } from '@vueuse/core'
import useI18n from '@app/shared/vue/composables/use-I18n'
import { useSearchable } from '@app/shared/vue/composables/use-searchable'
import { useDropdown } from '@app/shared/pinia/dropdown'
import { SfButtonDs } from '@silverfin/ui-components'
import SfDropdown from '@app/shared/vue/components/sf-dropdown/sf-dropdown.vue'
import SfDropdownItemGroup from '@app/shared/vue/components/sf-dropdown/sf-dropdown-item-group.vue'
import SfDropdownItem from '@app/shared/vue/components/sf-dropdown/sf-dropdown-item.vue'
import SfSearch from '@app/shared/vue/components/sf-search/search-input.vue'
import type { Option, OptionGroup } from './types'

interface MultiselectFilterProps {
  modelValue: (string | number)[]
  options: OptionGroup[]
  dropdownId?: string
  triggerLabel?: string
  triggerLabelCustomSuffix?: string
  placeholder?: string
}

const props = withDefaults(defineProps<MultiselectFilterProps>(), {
  dropdownId: 'multiselectFilter',
  triggerLabel: undefined,
  triggerLabelCustomSuffix: undefined,
  placeholder: '',
})

const I18n = useI18n()
const dropdownStore = useDropdown()

const emit =
  defineEmits<
    (event: 'update:modelValue', value: (string | number)[]) => void
  >()

const selectedValues = useVModel(props, 'modelValue', emit, {
  deep: true,
  passive: true,
})
const { currentDropdownId } = storeToRefs(dropdownStore)

const flattenedOptions = computed(() => props.options.flatMap((g) => g.options))

const triggerDisplayValue = computed(() => {
  const selectedLabels = flattenedOptions.value.reduce((arr, option) => {
    if (selectedValues.value.includes(option.value)) {
      arr.push(option.label)
    }

    return arr
  }, [] as string[])

  if (props.triggerLabel && selectedLabels.length > 0) {
    const firstItem = `${props.triggerLabel}: ${selectedLabels[0]}`
    return selectedLabels.length === 1
      ? firstItem
      : `${firstItem} +${selectedLabels.length - 1}`
  } else if (props.triggerLabelCustomSuffix) {
    return `${props.triggerLabel}: ${props.triggerLabelCustomSuffix}`
  } else if (props.triggerLabel) {
    return `${props.triggerLabel}: ${I18n.t(
      'inbox.filters.note_types.labels.any'
    )}`
  } else {
    return I18n.t('inbox.filters.select')
  }
})

const { searchValue, results: filteredFlatOptions } = useSearchable<
  Option,
  Option
>(flattenedOptions, {
  prepareItem: (item) => item,
  fuzzySortOptions: { keys: ['label'] },
})

const collection = computed(() => {
  // If search value is empty, return the full collection with classes
  if (!searchValue.value) {
    return props.options
  }

  return props.options.reduce((arr, group) => {
    const filteredGroup = group.options.filter((option) =>
      filteredFlatOptions.value.some(
        (filteredItem) => filteredItem.value === option.value
      )
    )

    if (filteredGroup.length > 0) {
      arr.push({ id: group.id, options: filteredGroup })
    }

    return arr
  }, [] as OptionGroup[])
})

const isSelected = computed(() => {
  return (value: string | number) => selectedValues.value.includes(value)
})

const isScopedDropdownOpen = computed(() => {
  return currentDropdownId.value === props.dropdownId
})

function handleToggleDropdown() {
  dropdownStore.toggleDropdown(props.dropdownId)
}

function clearSearch(): void {
  searchValue.value = ''
}

function clearSelected(): void {
  selectedValues.value = []
}

function toggleSelection(value: string | number): void {
  const index = selectedValues.value.indexOf(value)

  if (index === -1) {
    selectedValues.value.push(value)
  } else {
    selectedValues.value.splice(index, 1)
  }
}
</script>
